// src/components/CollectionDetail.jsx
import ValuationCaption from "./ValuationCaption";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { getCollectionsPathByContext } from "@/utils/getCollectionsPathByContext";
import UseMyPhoneModal from "@/components/artwork/modals/UseMyPhoneModal";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { isCabinetEnabled } from "../lib/cabinetService";
import { useTranslation } from "react-i18next";
import DeleteCollectionModal from "../components/DeleteCollectionModal";
import ArtworksDashboard from "./ArtworksDashboard";
import CollectionActionsSidebar from "../components/CollectionActionsSidebar";
import DigitalMixHint from "../components/DigitalMixHint";
import CollectionReadinessCard from "../components/CollectionReadinessCard";
import CollectionConcentration from "../components/CollectionConcentration";
import CollectionIntakeCard from "../components/CollectionIntakeCard";
import CollectionHeritageAudit from "../components/CollectionHeritageAudit";
import ValueCoherenceAlert from "../components/ValueCoherenceAlert";
import ShareAccessModal from "../components/ShareAccessModal";
import SharePresentationModal from "../components/SharePresentationModal";
import LotCreator from "../components/LotCreator";
import OcrBatchEnrich from "../components/OcrBatchEnrich";
import CollectionTypeBadge from "./CollectionTypeBadge";
import MarketReleaseCard from "../components/MarketReleaseCard";

const ARTWORK_FALLBACK = "/images/artworks/Vignette_oeuvre_Image_Not_Available.png";
// ⚠️ En mode expert/partage, évite select("*") (risque: colonne non lisible => liste vide)
// Garde uniquement des champs "safe" (existants et utiles pour l'UI)
const ARTWORKS_SELECT_SAFE = [
  "id",
  "title",
  "artist",
  "year",
  "medium",
  "dimensions",
  "description",
  "collection_id",
  "client_id",
  "created_at",
  "updated_at",
  "image_url",
  "images",
  "tags",
  "value",
  "estimated_value",
  "valuation_estimated_value",
  "valuation_consensus",
  "valuation_market_value",
  "current_estimated_value",
  "valuation_low",
  "valuation_high",
  "cup_comparables",
  "acquisitionDetails",
  "location_id",
  "inventory_number",
  "condition",
  "is_draft",
].join(",");

// Même logique que ExpertReportGenerator — source unique de vérité pour le total estimé
function getCupObj(artwork) {
  const c = artwork.cup_comparables;
  if (!c) return null;
  if (typeof c === "string") {
    try { const o = JSON.parse(c); return (o && !Array.isArray(o)) ? o : null; }
    catch { return null; }
  }
  return (typeof c === "object" && !Array.isArray(c)) ? c : null;
}

function getEstVal(artwork) {
  if (artwork.valuation_estimated_value) return Number(artwork.valuation_estimated_value);
  if (artwork.current_estimated_value)   return Number(artwork.current_estimated_value);
  if (artwork.valuation_consensus)        return Number(artwork.valuation_consensus);
  if (artwork.valuation_market_value)     return Number(artwork.valuation_market_value);
  const obj = getCupObj(artwork);
  if (obj?.valuation_retained)            return Number(obj.valuation_retained);
  const lo = artwork.valuation_low  ? Number(artwork.valuation_low)  : null;
  const hi = artwork.valuation_high ? Number(artwork.valuation_high) : null;
  if (lo && hi) return Math.round((lo + hi) / 2);
  if (lo) return lo;
  if (hi) return hi;
  if (artwork.estimated_value != null)    return Number(artwork.estimated_value);
  if (artwork.value != null)              return Number(artwork.value);
  return null;
}

// Debug helpers
const DBG_ENABLED = false;
function DBG(scope, event, payload) {
  if (!DBG_ENABLED) return;
  const ts = new Date().toISOString();
  const header = `[${ts}] [${scope}] ${event}`;
  if (payload !== undefined) {
    console.groupCollapsed(header);
    console.log(payload);
    console.groupEnd();
  } else {
    console.log(header);
  }
}


const CollectionDetail = () => {
  const { t: translate, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || undefined;
  const isTFunction = typeof translate === "function";

  const params = useParams();
  const id = params.collectionId || params.id;

  const navigate = useNavigate();
  const location = useLocation();
  const [showPhoneNew, setShowPhoneNew] = useState(false); // passerelle QR : nouvelle œuvre par photo

  // Lit le filtre artiste depuis l'URL (?artist=Hermann%20NITSCH)
  const initialArtistFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("artist") || "";
  }, [location.search]);

  const { user, profile, loading: authLoading, profileLoading } = useAuth();

  // Cabinet de curiosités : lien visible seulement si la fonctionnalité est ouverte pour ce compte
  const [cabinetOn, setCabinetOn] = useState(false);
  useEffect(() => {
    let alive = true;
    if (!user?.id) return undefined;
    isCabinetEnabled().then((ok) => { if (alive) setCabinetOn(ok); });
    return () => { alive = false; };
  }, [user?.id]);

  const collectionsBackPath = getCollectionsPathByContext(location.pathname, profile);
  const isExpert = (
    profile?.role === "expert" ||
    profile?.role === "art_advisor" ||
    profile?.plan === "art_advisor" ||
    profile?.plan === "art_advisor_premium"
  );

  // OcrBatchEnrich déclenche de vrais appels OpenAI facturés — même garde que
  // OcrEnrichButton sur la fiche œuvre (ArtworkDetail.jsx : hasPaidPlanForAI).
  const hasPaidPlanForAI = ["premium", "art_advisor_premium", "family_office"].includes(
    String(profile?.plan || "standard").trim().toLowerCase()
  );

  const [isLoading, setIsLoading] = useState(true);
  const [intakeVersion, setIntakeVersion] = useState(0); // relance l'analyse après le cadrage
  const [collection, setCollection] = useState(null);
  const [deposit, setDeposit] = useState(null);      // collectionneur : type = deposit
  const [thirdParty, setThirdParty] = useState(null); // fondation : foundation_deposit / foundation_founder

  // Dérivé depuis profile (déjà chargé par AuthContext)
  const isFoundation = profile?.foundation_mode === true;
  const [artworks, setArtworks] = useState([]);
  // Statistiques remontées par ArtworksDashboard, qui est la source de vérité
  // de la grille visible. Cela évite le bug "0 œuvre" en haut alors que
  // la grille charge bien 625 œuvres.
  const [dashboardStats, setDashboardStats] = useState(null);
  const [totalPrice, setTotalPrice] = useState({ estimated: 0, purchase: 0, insured: 0 });
  const [accessDenied, setAccessDenied] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [sharedOwnerId, setSharedOwnerId] = useState(null);

  const [delOpen, setDelOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [sharePresentationOpen, setSharePresentationOpen] = useState(false);
  const [lotCreatorOpen, setLotCreatorOpen] = useState(false);
  const [ocrBatchOpen,   setOcrBatchOpen]   = useState(false);

  const runIdRef = useRef(0);

  // Gestion des erreurs globales
  useEffect(() => {
    const onError = (msg, src, line, col, err) => {
      DBG("GLOBAL", "window.onerror", { msg, src, line, col, err: err?.stack || String(err) });
    };

    const onRejection = (e) => {
      DBG("GLOBAL", "unhandledrejection", { reason: e?.reason?.stack || String(e?.reason) });
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    DBG("GLOBAL", "mounted error traps");

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      DBG("GLOBAL", "unmounted error traps");
    };
  }, []);

  // Fonction de rafraîchissement des œuvres
  const refreshArtworks = () => {
    DBG("CollectionDetail", "refreshArtworks called", {
      hasId: !!id,
      userId: user?.id,
      tType: typeof translate,
    });

    if (!id || !user?.id) return;

    (async () => {
      DBG("CollectionDetail", "refreshArtworks query start", { id, userId: user.id });

      const { data: artworksData, error } = await supabase
        .from("artworks_secure")
        .select(ARTWORKS_SELECT_SAFE)
        .eq("collection_id", id);

      DBG("CollectionDetail", "refreshArtworks query end", {
        rows: artworksData?.length || 0,
        errorCode: error?.code,
        errorMsg: error?.message,
      });

      if (error) return;

      setArtworks(artworksData || []);

      let totalEstimated = 0;
      let totalPurchase = 0;

      (artworksData || []).forEach((art) => {
        const estVal = getEstVal(art);
        if (estVal != null && !Number.isNaN(estVal)) { const n = estVal; if (!Number.isNaN(n)) totalEstimated += n;
        }

        // Priorité : acquisition_price_eur (devise étrangère convertie) > acquisition_price > acquisitionDetails
        const purchaseEur = art.acquisition_price_eur ?? art.acquisition_price ?? null;
        if (purchaseEur != null) {
          const n = Number(String(purchaseEur).replace(/\s/g, "").replace(",", "."));
          if (!Number.isNaN(n)) totalPurchase += n;
        } else if (art.acquisitionDetails) {
          try {
            const parsed =
              typeof art.acquisitionDetails === "string"
                ? JSON.parse(art.acquisitionDetails)
                : art.acquisitionDetails;

            const priceRaw = parsed?.purchase_price ?? parsed?.purchasePrice ?? parsed?.price;
            if (priceRaw != null) {
              const n = Number(String(priceRaw).replace(/\s/g, "").replace(",", "."));
              if (!Number.isNaN(n)) totalPurchase += n;
            }
          } catch (e) {
            DBG("CollectionDetail", "refreshArtworks parse acquisitionDetails FAILED", {
              artworkId: art?.id,
              acquisitionDetails: art?.acquisitionDetails,
              err: e?.stack || String(e),
            });
          }
        }
      });

      setTotalPrice({ estimated: totalEstimated, purchase: totalPurchase, insured: 0 });

      DBG("CollectionDetail", "refreshArtworks totals", {
        totalEstimated,
        totalPurchase,
        count: artworksData?.length || 0,
      });
    })().catch((e) => {
      DBG("CollectionDetail", "refreshArtworks exception", { err: e?.stack || String(e) });
    });
  };

  // Fonction de calcul du prix total
  const fetchTotalPrice = async () => {
    DBG("CollectionDetail", "fetchTotalPrice start", { id });

    try {
      if (!id) return;

      const { data, error } = await supabase
        .from("artworks_secure")
        .select("value, estimated_value, valuation_estimated_value, valuation_consensus, valuation_market_value, current_estimated_value, valuation_low, valuation_high, cup_comparables, acquisitionDetails")
        .eq("collection_id", id);

      DBG("CollectionDetail", "fetchTotalPrice result", {
        rows: data?.length || 0,
        errorCode: error?.code,
        errorMsg: error?.message,
      });

      if (error) return;

      let totalEstimated = 0;
      let totalPurchase = 0;

      (data || []).forEach((a) => {
        const estVal2 = getEstVal(a);
        if (estVal2 != null && !Number.isNaN(estVal2)) { const n = estVal2; if (!Number.isNaN(n)) totalEstimated += n;
        }

        // Priorité : acquisition_price_eur (devise étrangère convertie) > acquisition_price > acquisitionDetails
        const purchaseEurFetch = a.acquisition_price_eur ?? a.acquisition_price ?? null;
        if (purchaseEurFetch != null) {
          const n = Number(String(purchaseEurFetch).replace(/\s/g, "").replace(",", "."));
          if (!Number.isNaN(n)) totalPurchase += n;
        } else if (a.acquisitionDetails) {
          try {
            const parsed =
              typeof a.acquisitionDetails === "string"
                ? JSON.parse(a.acquisitionDetails)
                : a.acquisitionDetails;

            const priceRaw = parsed?.purchase_price ?? parsed?.purchasePrice ?? parsed?.price;
            if (priceRaw != null) {
              const n = Number(String(priceRaw).replace(/\s/g, "").replace(",", "."));
              if (!Number.isNaN(n)) totalPurchase += n;
            }
          } catch (e) {
            DBG("CollectionDetail", "fetchTotalPrice parse error", {
              artworkId: a?.id,
              err: e?.stack || String(e),
            });
          }
        }
      });

      setTotalPrice({ estimated: totalEstimated, purchase: totalPurchase, insured: 0 });
      DBG("CollectionDetail", "fetchTotalPrice totals", { totalEstimated, totalPurchase });
    } catch (e) {
      DBG("CollectionDetail", "fetchTotalPrice exception", { err: e?.stack || String(e) });
    }
  };

  // CHARGEMENT PRINCIPAL
  useEffect(() => {
    if (authLoading || profileLoading) return;
    if (!id || !user?.id) return;

    let cancelled = false;
    const runId = ++runIdRef.current;

    DBG("CollectionDetail", "effect START", { runId, id, userId: user.id });

    const fetchCollectionData = async () => {
      try {
        setIsLoading(true);
        setAccessDenied(false);

        // 1️⃣ COLLECTION PROPRE ?
        const { data: ownedCollection, error: ownedError } = await supabase
          .from("collections")
          .select("*")
          .eq("id", id)
          .eq("client_id", user.id)
          .maybeSingle();

        if (ownedCollection && !ownedError) {
          if (cancelled) return;

          setCollection(ownedCollection);
          setCanEdit(true);

          // ── Charger les infos tiers selon le mode ──
          const ct = ownedCollection.collection_type;
          if (!isFoundation && ct === "deposit") {
            // Collectionneur : table deposits
            const { data: depData } = await supabase
              .from("deposits")
              .select("*")
              .eq("collection_id", id)
              .maybeSingle();
            if (!cancelled) { setDeposit(depData || null); setThirdParty(null); }
          } else if (isFoundation && (ct === "foundation_deposit" || ct === "foundation_founder")) {
            // Fondation : table collection_third_parties
            const { data: tpData } = await supabase
              .from("collection_third_parties")
              .select("*")
              .eq("collection_id", id)
              .maybeSingle();
            if (!cancelled) { setThirdParty(tpData || null); setDeposit(null); }
          } else {
            setDeposit(null);
            setThirdParty(null);
          }

          const { data: artworksData, error: artError } = await supabase
            .from("artworks")
            .select(ARTWORKS_SELECT_SAFE)
            .eq("collection_id", id)
            .eq("client_id", user.id);

          if (!artError) {
            const safeParseImages = (raw) => {
              if (!raw) return [];
              if (Array.isArray(raw)) return raw;
              if (typeof raw === "string") {
                try {
                  const parsed = JSON.parse(raw);
                  return Array.isArray(parsed) ? parsed : [];
                } catch {
                  return [];
                }
              }
              return [];
            };

            const pickImageFromArray = (arr) => {
              if (!Array.isArray(arr) || !arr.length) return null;
              const primary = arr.find((x) => x?.isPrimary || x?.is_primary) || arr[0];
              if (!primary) return null;
              if (typeof primary === "string") return primary;
              if (typeof primary === "object") {
                return (
                  primary.url ||
                  primary.path ||
                  primary.file ||
                  primary.publicUrl ||
                  primary.public_url ||
                  null
                );
              }
              return null;
            };

            const mappedArtworks = (artworksData || []).map((a) => {
              const imgArr = safeParseImages(a.images);
              const pickedImg = pickImageFromArray(imgArr);
              return {
                ...a,
                artiste: a.artiste || a.artist || "",
                thumbnail: pickedImg || a.image_url || ARTWORK_FALLBACK,
                cover_image: pickedImg || a.image_url || ARTWORK_FALLBACK,
                _display_image: pickedImg || a.image_url || ARTWORK_FALLBACK,
              };
            });

            setArtworks(mappedArtworks);
          }

          await fetchTotalPrice();
          if (!cancelled) setIsLoading(false);
          return;
        }

        // 2️⃣ PERMISSIONS EXPERT ?
        const { data: permission, error: permError } = await supabase
          .from("collection_permissions")
          .select("can_view, can_edit, client_id")
          .eq("collection_id", id)
          .eq("grantee_id", user.id)
          .maybeSingle();

        const hasAccess =
          !!permission && (permission.can_view === true || permission.can_edit === true);

        if (!hasAccess || permError) {
          // 2️⃣bis COLLABORATEUR (table collaborators)
          // Sans accessible_collection_ids (colonne inexistante)
          const { data: collabEntry } = await supabase
            .from("collaborators")
            .select("id, can_view, can_edit, can_add_artworks, can_delete_artworks, can_export, can_generate_reports, can_access_ai, collection_id, created_by")
            .eq("user_id", user.id)
            .eq("status", "active")
            .maybeSingle();

          // can_view null = non défini = autoriser si le compte est actif
          const collabCanView = collabEntry && collabEntry.can_view !== false;
          const collabHasAccess = collabCanView && (
            collabEntry.collection_id === id ||
            !collabEntry.collection_id // pas de restriction = toutes collections de l'owner
          );

          if (!collabHasAccess) {
            if (cancelled) return;
            setAccessDenied(true);
            setCollection(null);
            setArtworks([]);
            setIsLoading(false);
            return;
          }

          // Charger la collection via RPC SECURITY DEFINER (bypass RLS)
          const { data: collabCols } = await supabase
            .rpc("get_collaborator_collections", { p_collaborator_id: collabEntry.id });

          const collabCollection = (collabCols || []).find(c => c.id === id);

          if (!collabCollection) {
            if (cancelled) return;
            setAccessDenied(true);
            setCollection(null);
            setArtworks([]);
            setIsLoading(false);
            return;
          }

          if (cancelled) return;
          setCollection({ ...collabCollection, isShared: true, isCollaborator: true });
          setCanEdit(collabEntry.can_edit !== false);
          setSharedOwnerId(collabEntry.created_by || null);

          // Charger les artworks via RPC SECURITY DEFINER (bypass RLS)
          const { data: collabArtworks } = await supabase
            .rpc("get_collaborator_artworks", {
              p_collaborator_id: collabEntry.id,
              p_collection_id: id,
            });

          if (!cancelled) {
            setArtworks(collabArtworks || []);
            setIsLoading(false);
          }
          return;
        }

        // 3️⃣ CHARGER LA COLLECTION PARTAGÉE
        const { data: sharedCollection, error: collError } = await supabase
          .from("collections")
          .select("*")
          .eq("id", id)
          .maybeSingle();

        if (!sharedCollection || collError) {
          if (cancelled) return;
          setAccessDenied(true);
          setCollection(null);
          setArtworks([]);
          setIsLoading(false);
          return;
        }

        if (cancelled) return;
        setCollection({ ...sharedCollection, isShared: true });
        setCanEdit(!!permission.can_edit);
        setSharedOwnerId(permission.client_id || null);

        // 4️⃣ ŒUVRES
        const { data: artworksData, error: artError } = await supabase
          .from("artworks_secure")
          .select(ARTWORKS_SELECT_SAFE)
          .eq("collection_id", id);

        if (!artError) {
          const safeParseImages = (raw) => {
            if (!raw) return [];
            if (Array.isArray(raw)) return raw;
            if (typeof raw === "string") {
              try {
                const parsed = JSON.parse(raw);
                return Array.isArray(parsed) ? parsed : [];
              } catch {
                return [];
              }
            }
            return [];
          };

          const pickImageFromArray = (arr) => {
            if (!Array.isArray(arr) || !arr.length) return null;
            const primary = arr.find((x) => x?.isPrimary || x?.is_primary) || arr[0];
            if (!primary) return null;
            if (typeof primary === "string") return primary;
            if (typeof primary === "object") {
              return (
                primary.url ||
                primary.path ||
                primary.file ||
                primary.publicUrl ||
                primary.public_url ||
                null
              );
            }
            return null;
          };

          const mappedArtworks = (artworksData || []).map((a) => {
            const imgArr = safeParseImages(a.images);
            const pickedImg = pickImageFromArray(imgArr);
            return { ...a, _display_image: pickedImg || a.image_url || ARTWORK_FALLBACK };
          });

          setArtworks(mappedArtworks);
        }

        await fetchTotalPrice();
      } catch (e) {
        DBG("CollectionDetail", "fetchCollectionData EXCEPTION", {
          runId,
          err: e?.stack || String(e),
        });
        if (!cancelled) {
          setAccessDenied(true);
          setCollection(null);
          setArtworks([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchCollectionData();

    return () => {
      cancelled = true;
      DBG("CollectionDetail", "effect cleanup (cancelled)", { runId });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.id, authLoading, profileLoading]);

  // RENDER GUARDS
  if (!isTFunction) {
    return (
      <div style={{ padding: 48, textAlign: "center", fontFamily: "Georgia, serif" }}>
        <p style={{ color: "#991b1b" }}>{translate("round36.collectionDetail.translationUnavailable")}</p>
      </div>
    );
  }

  if (authLoading || profileLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fafaf8",
          fontFamily: "Georgia, serif",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 40,
              height: 40,
              border: "3px solid #e5e3df",
              borderTopColor: "#1a1a1a",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px",
            }}
          />
          <p style={{ color: "#6b6b6b", fontSize: 14 }}>{translate("round36.collectionDetail.loading")}</p>
        </div>
      </div>
    );
  }

  if (!user?.id) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fafaf8",
          fontFamily: "Georgia, serif",
        }}
      >
        <div
          style={{
            maxWidth: 480,
            padding: 48,
            background: "#fff",
            border: "1px solid #e5e3df",
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <h2
            style={{
              fontSize: 24,
              fontWeight: 400,
              marginBottom: 16,
              color: "#1a1a1a",
              letterSpacing: "-0.02em",
            }}
          >
            {translate("round36.collectionDetail.authRequired.title")}
          </h2>
          <p
            style={{
              fontSize: 14,
              color: "#6b6b6b",
              marginBottom: 24,
              lineHeight: 1.6,
              fontFamily: "system-ui, sans-serif",
            }}
          >
            {translate("round36.collectionDetail.authRequired.description")}
          </p>
          <button
            onClick={() => navigate("/login")}
            style={{
              padding: "12px 24px",
              background: "#1a1a1a",
              color: "#fff",
              border: "none",
              borderRadius: 2,
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
              fontFamily: "system-ui, sans-serif",
            }}
          >
            {translate("round36.collectionDetail.authRequired.button")}
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fafaf8",
          fontFamily: "Georgia, serif",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 40,
              height: 40,
              border: "3px solid #e5e3df",
              borderTopColor: "#1a1a1a",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px",
            }}
          />
          <p style={{ color: "#6b6b6b", fontSize: 14 }}>{translate("round36.collectionDetail.loadingCollection")}</p>
        </div>
      </div>
    );
  }

  if (accessDenied || !collection) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fafaf8",
          fontFamily: "Georgia, serif",
        }}
      >
        <div
          style={{
            maxWidth: 480,
            padding: 48,
            background: "#fff",
            border: "1px solid #e5e3df",
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <h2
            style={{
              fontSize: 24,
              fontWeight: 400,
              marginBottom: 16,
              color: "#1a1a1a",
              letterSpacing: "-0.02em",
            }}
          >
            {translate("round36.collectionDetail.notFound.title")}
          </h2>
          <p
            style={{
              fontSize: 14,
              color: "#6b6b6b",
              marginBottom: 24,
              lineHeight: 1.6,
              fontFamily: "system-ui, sans-serif",
            }}
          >
            {translate("round36.collectionDetail.notFound.description")}
          </p>
          <Link
            to={collectionsBackPath}
            style={{
              display: "inline-block",
              padding: "12px 24px",
              background: "#1a1a1a",
              color: "#fff",
              textDecoration: "none",
              borderRadius: 2,
              fontSize: 14,
              fontWeight: 500,
              fontFamily: "system-ui, sans-serif",
            }}
          >
            {translate("round36.collectionDetail.notFound.back")}
          </Link>
        </div>
      </div>
    );
  }

  const collectionName = collection.title || collection.nom || translate("round36.collectionDetail.unnamedCollection");

  // Propriétaire réel : accès complet, même si des flags de partage/permissions
  // sont présents par héritage ou mauvaise hydratation.
  const isCollectionOwner = Boolean(
    user?.id &&
      (collection.client_id === user.id ||
        collection.owner_id === user.id ||
        collection.created_by === user.id)
  );

  const effectiveCanEdit = canEdit || isCollectionOwner;
  const effectiveIsShared = Boolean(collection.isShared && !isCollectionOwner);
  const effectiveOwnerId = effectiveIsShared ? sharedOwnerId : collection.client_id || user?.id || null;
  const artworkCountForSummary = Number.isFinite(dashboardStats?.totalCount)
    ? dashboardStats.totalCount
    : artworks.length;

  return (
    <div
      style={{
        background: "#fafaf8",
        minHeight: "100vh",
        padding: "clamp(20px, 5vw, 48px) clamp(16px, 4vw, 32px)",
        fontFamily: "Georgia, serif",
      }}
    >
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: 40 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 16,
              flexWrap: "wrap",
              gap: 16,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1
                style={{
                  fontSize: "clamp(24px, 6vw, 42px)",
                  fontWeight: 300,
                  marginBottom: 8,
                  color: "#1a1a1a",
                  letterSpacing: "-0.03em",
                  wordBreak: "break-word",
                }}
              >
                {collectionName}
              </h1>

              {collection.isShared && (
                <div
                  style={{
                    display: "inline-block",
                    background: "#1a1a1a",
                    color: "#fff",
                    fontSize: 11,
                    padding: "4px 12px",
                    fontWeight: 600,
                    letterSpacing: "0.05em",
                    fontFamily: "system-ui, sans-serif",
                    marginTop: 8,
                  }}
                >
                  {translate("round36.collectionDetail.sharedWithYou")}
                </div>
              )}

              {/* ─── Badge type de collection ─── */}
              {collection.collection_type &&
                collection.collection_type !== "private" &&
                collection.collection_type !== "foundation_owned" && (
                  <div style={{ marginTop: 12 }}>
                    <CollectionTypeBadge
                      type={collection.collection_type}
                      size="large"
                      isFoundation={isFoundation}
                    />
                  </div>
                )}
            </div>

            <UseMyPhoneModal
              open={showPhoneNew}
              onClose={() => setShowPhoneNew(false)}
              actionType="new_artwork"
              collectionId={id}
              onCompleted={(row) => {
                if (row?.result_path) navigate(`/artwork/${row.result_path}/edit`);
              }}
            />
          </div>

          {collection.description && (
            <p
              style={{
                fontSize: 16,
                color: "#4a4a4a",
                lineHeight: 1.7,
                fontFamily: "system-ui, sans-serif",
                maxWidth: 800,
              }}
            >
              {collection.description}
            </p>
          )}
        </div>

        {/* ══════════════════════════════════════════════════════════════
            BANDEAUX DE STATUT JURIDIQUE
            Collectionneur : inalienable + deposit (inchangés)
            Fondation       : 4 types (jamais affichés si !isFoundation)
        ══════════════════════════════════════════════════════════════ */}

        {/* ─── Collectionneur : inaliénable ─── */}
        {!isFoundation && collection.collection_type === "inalienable" && (
          <div style={{ background: "#FAF8F3", border: "1px solid #E5D5A8", borderLeft: "3px solid #C9A961", padding: "20px 28px", marginBottom: 32, fontFamily: "Helvetica Neue, sans-serif" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
              <span style={{ color: "#C9A961", fontSize: 18, fontFamily: "Georgia, serif" }}>✦</span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: 16, color: "#1A1A1A" }}>{translate("round36.collectionDetail.banners.inalienable.title")}</span>
              {collection.legal_status && collection.legal_status !== "a_clarifier" && (
                <span style={{ fontSize: 11, color: "#6B6B6B", background: "#fff", padding: "3px 10px", border: "1px solid #E5D5A8", textTransform: "uppercase", letterSpacing: "1px" }}>
                  {translate(`round36.collectionDetail.legalStatus.${collection.legal_status}`, { defaultValue: collection.legal_status })}
                </span>
              )}
            </div>
            <p style={{ fontSize: 13, color: "#6B6B6B", lineHeight: 1.6, margin: 0, paddingLeft: 30 }}>
              {translate("round36.collectionDetail.banners.inalienable.description")}
              {collection.legal_status === "a_clarifier" && <span style={{ display: "block", marginTop: 6, color: "#C9A961" }}>{translate("round36.collectionDetail.banners.inalienable.clarify")}</span>}
              {collection.legal_notes && <span style={{ display: "block", marginTop: 8, color: "#4A4A4A", fontStyle: "italic" }}>{translate("round36.collectionDetail.reference", { value: collection.legal_notes })}</span>}
            </p>
          </div>
        )}

        {/* ─── Collectionneur : dépôt ─── */}
        {!isFoundation && collection.collection_type === "deposit" && (
          <div style={{ background: "#F4F4F2", border: "1px solid #D8D6CF", borderLeft: "3px solid #1A1A1A", padding: "20px 28px", marginBottom: 32, fontFamily: "Helvetica Neue, sans-serif" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
              <span style={{ color: "#1A1A1A", fontSize: 18, fontFamily: "Georgia, serif" }}>◇</span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: 16, color: "#1A1A1A" }}>{translate("round36.collectionDetail.banners.deposit.title")}</span>
              {deposit?.owner_name && <span style={{ fontSize: 13, color: "#6B6B6B" }}>{translate("round36.collectionDetail.ownerLabel")} <strong>{deposit.owner_name}</strong></span>}
            </div>
            <p style={{ fontSize: 13, color: "#6B6B6B", lineHeight: 1.6, margin: 0, paddingLeft: 30 }}>
              {translate("round36.collectionDetail.banners.deposit.description")}
              {!deposit && <span style={{ display: "block", marginTop: 6, color: "#1A1A1A" }}>{translate("round36.collectionDetail.banners.deposit.complete")}</span>}
              {deposit?.contract_end_date && <span style={{ display: "block", marginTop: 6 }}>{translate("round36.collectionDetail.validUntil", { date: new Date(deposit.contract_end_date).toLocaleDateString(locale) })}</span>}
            </p>
          </div>
        )}

        {/* ─── Fondation : propriété propre ─── */}
        {isFoundation && collection.collection_type === "foundation_owned" && (
          <div style={{ background: "#EBF3EF", border: "1px solid #B8D9CB", borderLeft: "3px solid #2C5F4A", padding: "20px 28px", marginBottom: 32, fontFamily: "Helvetica Neue, sans-serif" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
              <span style={{ color: "#2C5F4A", fontSize: 18, fontFamily: "Georgia, serif" }}>◉</span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: 16, color: "#1A1A1A" }}>{translate("round36.collectionDetail.banners.foundationOwned.title")}</span>
            </div>
            <p style={{ fontSize: 13, color: "#6B6B6B", lineHeight: 1.6, margin: 0, paddingLeft: 30 }}>
              {translate("round36.collectionDetail.banners.foundationOwned.description")}
            </p>
          </div>
        )}

        {/* ─── Fondation : prêt du fondateur ─── */}
        {isFoundation && collection.collection_type === "foundation_founder" && (
          <div style={{ background: "#FAF8F3", border: "1px solid #E8D9B0", borderLeft: "3px solid #C9A961", padding: "20px 28px", marginBottom: 32, fontFamily: "Helvetica Neue, sans-serif" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
              <span style={{ color: "#7A5C00", fontSize: 18, fontFamily: "Georgia, serif" }}>◈</span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: 16, color: "#1A1A1A" }}>{translate("round36.collectionDetail.banners.founderLoan.title")}</span>
              {thirdParty?.party_name && <span style={{ fontSize: 13, color: "#6B6B6B" }}>{translate("round36.collectionDetail.lenderLabel")} <strong>{thirdParty.party_name}</strong></span>}
            </div>
            <p style={{ fontSize: 13, color: "#6B6B6B", lineHeight: 1.6, margin: 0, paddingLeft: 30 }}>
              {translate("round36.collectionDetail.banners.founderLoan.description")}
              {thirdParty?.end_date && <span style={{ display: "block", marginTop: 6 }}>{translate("round36.collectionDetail.loanValidUntil", { date: new Date(thirdParty.end_date).toLocaleDateString(locale) })}</span>}
              {!thirdParty && <span style={{ display: "block", marginTop: 6, color: "#C9A961" }}>{translate("round36.collectionDetail.banners.founderLoan.complete")}</span>}
            </p>
          </div>
        )}

        {/* ─── Fondation : donation / legs ─── */}
        {isFoundation && collection.collection_type === "foundation_donation" && (
          <div style={{ background: "#F5ECE5", border: "1px solid #D9B89A", borderLeft: "3px solid #8B4513", padding: "20px 28px", marginBottom: 32, fontFamily: "Helvetica Neue, sans-serif" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
              <span style={{ color: "#8B4513", fontSize: 18, fontFamily: "Georgia, serif" }}>✦</span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: 16, color: "#1A1A1A" }}>{translate("round36.collectionDetail.banners.donation.title")}</span>
              {collection.legal_status && collection.legal_status !== "a_clarifier" && (
                <span style={{ fontSize: 11, color: "#6B6B6B", background: "#fff", padding: "3px 10px", border: "1px solid #D9B89A", textTransform: "uppercase", letterSpacing: "1px" }}>
                  {translate(`round36.collectionDetail.legalStatus.${collection.legal_status}`, { defaultValue: collection.legal_status })}
                </span>
              )}
            </div>
            <p style={{ fontSize: 13, color: "#6B6B6B", lineHeight: 1.6, margin: 0, paddingLeft: 30 }}>
              {translate("round36.collectionDetail.banners.donation.description")}
              {collection.legal_notes && <span style={{ display: "block", marginTop: 8, color: "#4A4A4A", fontStyle: "italic" }}>{translate("round36.collectionDetail.reference", { value: collection.legal_notes })}</span>}
              {collection.legal_status === "a_clarifier" && <span style={{ display: "block", marginTop: 6, color: "#8B4513" }}>{translate("round36.collectionDetail.banners.donation.clarify")}</span>}
            </p>
          </div>
        )}

        {/* ─── Fondation : dépôt institution / État ─── */}
        {isFoundation && collection.collection_type === "foundation_deposit" && (
          <div style={{ background: "#E5EBF3", border: "1px solid #A8BDD9", borderLeft: "3px solid #1C3A5E", padding: "20px 28px", marginBottom: 32, fontFamily: "Helvetica Neue, sans-serif" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
              <span style={{ color: "#1C3A5E", fontSize: 18, fontFamily: "Georgia, serif" }}>◇</span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: 16, color: "#1A1A1A" }}>{translate("round36.collectionDetail.banners.foundationDeposit.title")}</span>
              {thirdParty?.party_name && <span style={{ fontSize: 13, color: "#6B6B6B" }}>{translate("round36.collectionDetail.ownerLabel")} <strong>{thirdParty.party_name}</strong></span>}
              {collection.legal_status && collection.legal_status !== "a_clarifier" && (
                <span style={{ fontSize: 11, color: "#1C3A5E", background: "#fff", padding: "3px 10px", border: "1px solid #A8BDD9", textTransform: "uppercase", letterSpacing: "1px" }}>
                  {translate(`round36.collectionDetail.legalStatus.${collection.legal_status}`, { defaultValue: collection.legal_status })}
                </span>
              )}
            </div>
            <p style={{ fontSize: 13, color: "#6B6B6B", lineHeight: 1.6, margin: 0, paddingLeft: 30 }}>
              {translate("round36.collectionDetail.banners.foundationDeposit.description")}
              {thirdParty?.contract_ref && <span style={{ display: "block", marginTop: 6 }}>{translate("round36.collectionDetail.convention", { value: thirdParty.contract_ref })}</span>}
              {thirdParty?.end_date && <span style={{ display: "block", marginTop: 4 }}>{translate("round36.collectionDetail.validUntil", { date: new Date(thirdParty.end_date).toLocaleDateString(locale) })}</span>}
              {!thirdParty && <span style={{ display: "block", marginTop: 6, color: "#1C3A5E" }}>{translate("round36.collectionDetail.banners.foundationDeposit.complete")}</span>}
            </p>
          </div>
        )}

        {effectiveCanEdit && !effectiveIsShared && <DigitalMixHint collectionId={id} />}

        {/* Layout 2 colonnes : sidebar d'actions (gauche) + contenu (droite) */}
        <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
          <CollectionActionsSidebar
            t={translate}
            id={id}
            collectionName={collectionName}
            ownerName={collection?.client_nom || `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim()}
            canEdit={effectiveCanEdit}
            isExpert={isExpert}
            isShared={effectiveIsShared}
            editUrl={`/collections/${collection.id}/edit`}
            presentationUrl={`/presentation/collection/${collection.id}`}
            cabinetUrl={cabinetOn && effectiveCanEdit && !effectiveIsShared ? `/collections/${collection.id}/cabinet` : null}
            onLot={() => setLotCreatorOpen(true)}
            onPhone={() => setShowPhoneNew(true)}
            onShareAccess={() => setShareOpen(true)}
            onSharePresentation={() => setSharePresentationOpen(true)}
            onExpertise={() => navigate(`/expert-report?collectionId=${collection.id}&collectionName=${encodeURIComponent(collection.title || "")}&clientName=${encodeURIComponent(collection.isShared ? (collection.client_nom || "") : ((profile?.first_name || "") + " " + (profile?.last_name || "")).trim())}`)}
            onReport={() => navigate(`/expert-report?collectionId=${id}&collectionName=${encodeURIComponent(collectionName || "")}`)}
            onOcr={() => {
              if (!hasPaidPlanForAI) { navigate("/pricing"); return; }
              setOcrBatchOpen(true);
            }}
            onDelete={() => setDelOpen(true)}
          />
          <div style={{ flex: "1 1 480px", minWidth: 0 }}>

        {/* Score de santé de la collection (moteur readiness) — §ARTDAI Priorité 1+2 */}
        <CollectionReadinessCard userId={effectiveOwnerId} collectionId={id} />

        {/* Alerte incohérence de valeurs (§ARTDAI Priorité 2, via traçabilité §P3) */}
        <ValueCoherenceAlert collectionId={id} />

        {/* Cadrage de la collection : 4 questions d'accueil */}
        <CollectionIntakeCard collectionId={id} ownerId={effectiveOwnerId} onSaved={() => setIntakeVersion((v) => v + 1)} />

        {/* Audit patrimonial : propriétaire, statut, provenance, pièces (ensembles de galerie, archives, estates) */}
        <CollectionHeritageAudit collectionId={id} />

        {/* Cadence de mise sur le marché (estate, collection familiale écoulée progressivement) */}
        <MarketReleaseCard collectionId={id} />

        {/* Concentration de la collection — §ARTDAI Priorité 5 */}
        <CollectionConcentration key={intakeVersion} collectionId={id} defaultCurrency="EUR" />

        {/* Stats */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "clamp(12px, 3vw, 24px)",
            marginBottom: 48,
          }}
        >
          <div
            style={{
              background: "#fff",
              border: "1px solid #e5e3df",
              borderRadius: 2,
              padding: "clamp(14px, 3vw, 24px)",
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: "#6b6b6b",
                marginBottom: 8,
                fontFamily: "system-ui, sans-serif",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {translate("round36.collectionDetail.stats.artworks")}
            </div>
            <div
              style={{
                fontSize: "clamp(22px, 5vw, 32px)",
                fontWeight: 300,
                color: "#1a1a1a",
                letterSpacing: "-0.02em",
              }}
            >
              {artworkCountForSummary}
            </div>
          </div>

          <div
            style={{
              background: "#fff",
              border: "1px solid #e5e3df",
              borderRadius: 2,
              padding: "clamp(14px, 3vw, 24px)",
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: "#6b6b6b",
                marginBottom: 8,
                fontFamily: "system-ui, sans-serif",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {translate("round36.collectionDetail.stats.estimatedValue")}
            </div>
            <div
              style={{
                fontSize: "clamp(16px, 4vw, 32px)",
                fontWeight: 300,
                color: "#1a1a1a",
                letterSpacing: "-0.02em",
                wordBreak: "break-word",
              }}
            >
              {new Intl.NumberFormat(locale, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(totalPrice.estimated || 0)}
            </div>
            <ValuationCaption />
          </div>
        </div>

        {/* Artworks Dashboard */}
        <ArtworksDashboard
          initialCollectionId={id}
          initialArtistFilter={initialArtistFromUrl}
          hideCollectionFilter={true}
          isSharedCollection={effectiveIsShared}
          collectionOwnerId={effectiveOwnerId}
          onImportDone={refreshArtworks}
          onStatsChange={(stats) => setDashboardStats(stats)}
        />
          </div>
        </div>

        {/* Modals */}
        {ocrBatchOpen && hasPaidPlanForAI && (
          <OcrBatchEnrich
            collectionId={collection.id}
            clientId={effectiveOwnerId || user.id}
            onClose={() => setOcrBatchOpen(false)}
          />
        )}

        {lotCreatorOpen && (
          <LotCreator
            collectionId={collection.id}
            clientId={effectiveOwnerId || user.id}
            onClose={() => setLotCreatorOpen(false)}
            onCreated={() => { setLotCreatorOpen(false); refreshArtworks(); }}
          />
        )}

        {delOpen && (
          <DeleteCollectionModal
            collection={collection}
            onClose={() => setDelOpen(false)}
            onDeleted={() => {
              setDelOpen(false);
              navigate(collectionsBackPath);
            }}
          />
        )}

        {shareOpen && (
          <ShareAccessModal 
            open={true}
            collection={collection}
            collectionId={collection?.id}
            collectionTitle={collection?.name || collection?.title}
            onClose={() => setShareOpen(false)} 
          />
        )}

        {sharePresentationOpen && (
          <SharePresentationModal
            collection={collection}
            onClose={() => setSharePresentationOpen(false)}
          />
        )}
      </div>
    </div>
  );
};



export default CollectionDetail;